import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import qs from 'qs';

import { media } from 'context';
import Button from 'ds/Button';
import Clickable from 'ds/Clickable';
import Icon from 'ds/Icon';
import Layout from 'ds/Layout';
import Modal from 'ds/Modal';
import Text from 'ds/Text';
import TextArea from 'ds/inputs/TextArea';
import { selectUser } from 'store/User/selectors';
import { useAppSelector } from 'store/hooks';

interface LikedConfig {
  doneText: string;
  promptText: string;
}

interface Props {
  isVisible: boolean;
  onClose: VoidFunction;
  liked?: boolean | null;
  handleSubmit: (params: { liked: boolean; message?: string }) => void;
  likedConfig: LikedConfig;
  notLikedConfig: LikedConfig;
  prompt: React.ReactNode;
}

const FeedbackModal: React.FC<Props> = ({ prompt, isVisible, onClose, likedConfig, notLikedConfig, ...props }) => {
  const { xs } = useContext(media);

  const history = useHistory();
  const queryParams = qs.parse(history.location.search, { ignoreQueryPrefix: true });
  const { thumbs_up, thumbs_down } = queryParams;

  const initialLikedValue = thumbs_up !== undefined ? true : thumbs_down !== undefined ? false : !!props.liked;
  const [updatedLiked, setUpdatedLiked] = useState<boolean | null>(null);
  const [message, setMessage] = useState<string>();
  const [requestInProgress, setRequestInProgress] = useState<boolean>();
  const [viewState, setViewState] = useState<'form' | 'success'>('form');

  const user = useAppSelector(selectUser);
  const userId = user?.id;
  if (!userId) return null;

  const liked = updatedLiked ?? initialLikedValue;

  const isValid = liked || (!liked && !!message);

  const handleSubmit = async () => {
    if (!isValid || requestInProgress || liked === null) return;

    setRequestInProgress(true);
    await props.handleSubmit({ liked, message });

    setViewState('success');
    setRequestInProgress(false);
  };

  return (
    <Modal
      header=" "
      width={xs ? undefined : 500}
      isVisible={isVisible}
      onClose={onClose}
      hasFooterBorder={false}
      animate={true}
      footer={
        !xs ? undefined : viewState === 'form' ? (
          <Button
            fullWidth
            type="primary"
            text="Submit"
            onClick={handleSubmit}
            disabled={!isValid || requestInProgress}
          />
        ) : (
          <Button fullWidth type="primary" text="Got it" onClick={onClose} />
        )
      }
      {...(xs ? { hasBottomSheetPaddingX: true, isBottomSheet: true, bottomSheetIsRounded: true } : { minHeight: 380 })}
    >
      {viewState === 'success' ? (
        <Layout
          direction="column"
          justify={xs ? 'center' : 'space-between'}
          align="center"
          height={xs ? '100%' : undefined}
          marginTop={xs ? 80 : undefined}
        >
          <Layout width={80} height={80} borderRadius="circular" color="blue-gradient" justify="center" align="center">
            <Icon name="checkmark" size="xl" color="white" />
          </Layout>
          <Layout marginTop={xs ? 32 : 16} />
          <Text size="headline-md" align="center">
            {liked ? 'Thanks' : 'Bummer'}, {user.firstname}!
          </Text>
          <Layout marginTop={16} />
          <Layout maxWidth={380}>
            <Text size="body-md" color="gray-700" align="center">
              {liked ? likedConfig.doneText : notLikedConfig.doneText}
            </Text>
          </Layout>
          {!xs && (
            <Layout marginTop={32} paddingY={16} paddingX={24}>
              <Button size="md" type="primary" fullWidth={xs} text="Got it" onClick={onClose} />
            </Layout>
          )}
        </Layout>
      ) : (
        <Layout direction="column" justify="space-between" align="center">
          <Layout direction="column" justify="space-between" align="center">
            {prompt}
            <Layout marginTop={xs ? 36 : 16} />
            <Layout direction="row" justify="center" align="center">
              <Clickable
                color={liked ? 'blue-500' : 'gray-25'}
                hoverColor={liked ? 'blue-600' : 'gray-50'}
                style={{
                  borderRadius: '50%',
                  width: 76,
                  height: 76,
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'inline-flex'
                }}
                onClick={() => setUpdatedLiked(true)}
                disabled={liked}
                context="thumbsUp-Modal"
              >
                <Icon
                  name={liked ? 'thumbsUpFilled' : 'thumbsUp'}
                  stroke={liked ? 1.5 : 2}
                  color={liked ? 'white' : 'black'}
                  size="lg"
                />
              </Clickable>
              <Layout marginLeft={20} />
              <Clickable
                color={!liked ? 'blue-500' : 'gray-25'}
                hoverColor={!liked ? 'blue-600' : 'gray-50'}
                style={{
                  borderRadius: '50%',
                  width: 76,
                  height: 76,
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'inline-flex'
                }}
                onClick={() => setUpdatedLiked(false)}
                disabled={!liked}
                context="thumbsDown-Modal"
              >
                <Icon
                  name={!liked ? 'thumbsDownFilled' : 'thumbsDown'}
                  stroke={!liked ? 1.5 : 2}
                  color={!liked ? 'white' : 'black'}
                  size="lg"
                />
              </Clickable>
            </Layout>
            <Layout marginTop={xs ? 36 : 16} />
            <Text size="body-sm" semibold>
              {liked ? likedConfig.promptText : notLikedConfig.promptText}
            </Text>
            <Layout marginTop={xs ? 12 : 16} />
            <Layout width="100%">
              <TextArea
                size="md"
                minHeight={112}
                value={message}
                onChange={({ value }) => setMessage(value)}
                label="Comment"
                required={liked === false}
                placeholder="Comment"
                hideLabel
              />
            </Layout>
          </Layout>
          {!xs && (
            <Layout marginTop={32} paddingY={16} paddingX={24}>
              <Button
                size="md"
                type="primary"
                fullWidth={xs}
                text="Submit"
                onClick={handleSubmit}
                disabled={!isValid || requestInProgress}
              />
            </Layout>
          )}
        </Layout>
      )}
    </Modal>
  );
};

export default FeedbackModal;
